type RuntimeEnvLike = {
  DEV?: boolean;
  VITE_API_URL?: string;
};

type RuntimeLocationLike = {
  origin?: string;
};

const LOCAL_PROXY_TARGET_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  '::1',
  '[::1]',
]);

const isPrivateIpv4Host = (value: string): boolean => {
  const octets = value.split('.').map((part) => Number.parseInt(part, 10));
  if (octets.length !== 4 || octets.some((octet) => Number.isNaN(octet) || octet < 0 || octet > 255)) {
    return false;
  }

  if (octets[0] === 10) {
    return true;
  }

  if (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) {
    return true;
  }

  return octets[0] === 192 && octets[1] === 168;
};

const isLocalProxyTargetHost = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  return LOCAL_PROXY_TARGET_HOSTS.has(normalized) || isPrivateIpv4Host(normalized);
};

export const shouldUseDevApiProxy = (
  env: RuntimeEnvLike,
  location?: RuntimeLocationLike | null,
): boolean => {
  const rawApiUrl = env.VITE_API_URL?.trim();
  const currentOrigin = location?.origin?.trim();

  if (!env.DEV || !rawApiUrl || !currentOrigin) {
    return false;
  }

  try {
    const apiUrl = new URL(rawApiUrl);
    if (apiUrl.origin === currentOrigin) {
      return false;
    }

    return isLocalProxyTargetHost(apiUrl.hostname);
  } catch {
    return false;
  }
};

export const resolveApiBaseUrlForRuntime = (
  env: RuntimeEnvLike,
  location?: RuntimeLocationLike | null,
): string => {
  const rawApiUrl = env.VITE_API_URL?.trim();
  const currentOrigin = location?.origin?.trim();

  if (shouldUseDevApiProxy(env, location) && currentOrigin) {
    return currentOrigin;
  }

  if (rawApiUrl) {
    return rawApiUrl;
  }

  return currentOrigin ?? '';
};

export const resolveApiBaseUrl = (): string => {
  const runtimeEnv = (import.meta as { env?: RuntimeEnvLike }).env ?? {};
  const runtimeLocation =
    typeof window !== 'undefined' && window.location
      ? { origin: window.location.origin }
      : undefined;

  return resolveApiBaseUrlForRuntime(runtimeEnv, runtimeLocation);
};
